import { useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  IndianRupee,
  Users,
  Clock,
  Building2,
  ChefHat,
  Truck,
  Sparkles,
  Send,
  TrendingUp,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useReveal } from "../hooks/useReveal";
import { BRAND } from "../data/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MODELS = [
  {
    id: "express-takeaway",
    name: "Express Takeaway",
    tagline: "Compact. Fast. High Footfall.",
    icon: Truck,
    recommended: false,
    image: "/franchise/express.png",
    summary:
      "A lean, takeaway-first kiosk built for high-street locations, food courts and metro corridors. Quick service, minimal seating, maximum throughput.",
    specs: [
      { icon: Building2, label: "Space", value: "200 – 400 sq ft" },
      { icon: IndianRupee, label: "Investment", value: "₹5 Lakh" },
      { icon: Users, label: "Team Size", value: "3 – 4 people" },
      { icon: Clock, label: "Setup", value: "30 – 45 days" },
      { icon: TrendingUp, label: "Payback", value: "8 – 12 months" },
    ],
    features: [
      "Counter ordering + digital menu screen",
      "Two-handi parallel kitchen layout",
      "Aggregator-ready (Swiggy / Zomato) on day one",
      "Brand kit, packaging, uniforms & POS included",
      "Central commissary supply, weekly delivery",
    ],
    bestFor: "Tier-1 high streets, IT parks, transit hubs, food courts",
  },
  {
    id: "standard-restaurant",
    name: "Standard Restaurant",
    tagline: "Full Dine-In. Brand Flagship.",
    icon: Building2,
    recommended: false,
    image: "/franchise/standard.png",
    summary:
      "A full-service dine-in flagship with bar-bench seating, family booths and an open dum kitchen. Built to anchor a neighbourhood as a destination biryani brand.",
    specs: [
      { icon: Building2, label: "Space", value: "600 – 1,200 sq ft" },
      { icon: IndianRupee, label: "Investment", value: "₹15 – 20 Lakh" },
      { icon: Users, label: "Team Size", value: "8 – 12 people" },
      { icon: Clock, label: "Setup", value: "60 – 90 days" },
      { icon: TrendingUp, label: "Payback", value: "18 – 24 months" },
    ],
    features: [
      "40 – 70 cover dine-in with private booths",
      "Open dum-cooking display kitchen",
      "Full menu — biryanis, starters, sides, desserts",
      "Bar-ready layout (optional liquor license)",
      "Dedicated dine-in + delivery + catering streams",
    ],
    bestFor: "Premium high streets, mall food avenues, lifestyle precincts",
  },
  {
    id: "cloud-kitchen",
    name: "Cloud Kitchen",
    tagline: "Delivery-Only. Low CAPEX. Multi-Brand.",
    icon: ChefHat,
    recommended: true,
    image: "/franchise/cloud.png",
    summary:
      "Our most recommended model. A delivery-only kitchen optimised for aggregator economics — lowest CAPEX, fastest break-even, and the option to run a 2nd virtual brand from the same kitchen.",
    specs: [
      { icon: Building2, label: "Space", value: "300 – 500 sq ft" },
      { icon: IndianRupee, label: "Investment", value: "₹6 – 8 Lakh" },
      { icon: Users, label: "Team Size", value: "4 – 6 people" },
      { icon: Clock, label: "Setup", value: "30 – 45 days" },
      { icon: TrendingUp, label: "Payback", value: "10 – 14 months" },
    ],
    features: [
      "Delivery-only, no front-of-house",
      "Optimised for Swiggy / Zomato / direct app",
      "Run 2nd virtual brand from the same kitchen",
      "Lower rent, lower overhead, higher margin",
      "Smart packaging that holds heat for 45 min",
    ],
    bestFor: "Residential clusters, secondary streets, B-grade real estate",
  },
];

function ModelCard({ m, idx, onInquire }) {
  const Icon = m.icon;
  return (
    <article
      data-testid={`franchise-model-${m.id}`}
      className={`reveal relative grid md:grid-cols-12 gap-6 md:gap-10 bg-white border rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow ${
        m.recommended
          ? "border-[#C8A14B] ring-2 ring-[#C8A14B]/30"
          : "border-cream-deep"
      }`}
      style={{ transitionDelay: `${idx * 60}ms` }}
    >
      {m.recommended && (
        <div
          data-testid={`franchise-recommended-${m.id}`}
          className="absolute top-4 right-4 z-10 inline-flex items-center gap-1.5 bg-[#C0392B] text-white px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold shadow-lg"
        >
          <Sparkles size={12} className="text-[#FFE6A8]" /> Highly Recommended
        </div>
      )}
      <div className="md:col-span-5 relative">
        <img
          src={m.image}
          alt={m.name}
          className="w-full h-64 md:h-full object-cover"
          loading="lazy"
        />
        <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-[#3A1B0E]/85 text-[#FAF3E7] backdrop-blur px-3 py-1.5 rounded-full">
          <span className="text-[10px] uppercase tracking-[0.28em] text-[#D4A24E]">
            Model 0{idx + 1}
          </span>
        </div>
        <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
          <div className="inline-flex items-center gap-2 bg-[#FAF3E7]/95 backdrop-blur px-3 py-2 rounded-full">
            <Icon size={16} className="text-gold" />
            <span className="text-xs font-semibold text-cocoa uppercase tracking-wider">
              {m.tagline}
            </span>
          </div>
        </div>
      </div>

      <div className="md:col-span-7 p-7 md:p-9 md:pl-2">
        <h3
          data-testid={`franchise-model-name-${m.id}`}
          className="font-display text-3xl md:text-4xl text-cocoa leading-tight"
        >
          {m.name}
        </h3>
        <p className="mt-3 text-cocoa-soft leading-relaxed">{m.summary}</p>

        <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-3">
          {m.specs.map((s) => {
            const SI = s.icon;
            return (
              <div
                key={s.label}
                className="rounded-xl border border-cream-deep bg-[#FAF3E7] p-3"
              >
                <SI size={16} className="text-gold" />
                <div className="text-[10px] uppercase tracking-wider text-cocoa-soft mt-2">
                  {s.label}
                </div>
                <div className="text-sm font-semibold text-cocoa mt-0.5">
                  {s.value}
                </div>
              </div>
            );
          })}
        </div>

        <ul className="mt-6 grid sm:grid-cols-2 gap-2.5">
          {m.features.map((f) => (
            <li key={f} className="flex gap-2 items-start text-sm text-cocoa">
              <CheckCircle2
                size={16}
                className="text-[#2E7D32] shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <div className="mt-6 flex items-center justify-between flex-wrap gap-3 border-t border-cream-deep pt-5">
          <div className="text-xs text-cocoa-soft">
            <span className="font-semibold text-cocoa">Best for:</span>{" "}
            {m.bestFor}
          </div>
          <button
            onClick={() => onInquire(m.id)}
            data-testid={`franchise-inquire-btn-${m.id}`}
            className="inline-flex items-center gap-2 rounded-full bg-[#C0392B] text-white hover:bg-[#D03A2A] hover:-translate-y-0.5 transition-all px-5 py-2.5 text-sm font-semibold shadow-md"
          >
            Inquire about this Model
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </article>
  );
}

export default function FranchisePage() {
  const rootRef = useReveal();
  const [params] = useSearchParams();
  const initialModel = params.get("model") || "";
  const formRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    model: initialModel && MODELS.some((m) => m.id === initialModel) ? initialModel : "",
    investment_range: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // submitted inquiry

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleInquire = (modelId) => {
    setForm((f) => ({ ...f, model: modelId }));
    setSuccess(null);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.city || !form.model) {
      toast.error("Please fill name, phone, city and select a model.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        email: form.email || null,
        investment_range: form.investment_range || null,
        message: form.message || null,
      };
      const res = await axios.post(`${API}/franchise-inquiries`, payload);
      toast.success("Inquiry received! Our team will reach out within 24 hours.");
      setSuccess(res.data);
      setForm({
        name: "",
        phone: "",
        email: "",
        city: "",
        model: "",
        investment_range: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Could not submit. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const stats = useMemo(
    () => [
      { label: "Outlets Operational", value: "4+" },
      { label: "Cities", value: "4" },
      { label: "Avg. Daily Handis", value: "2,400" },
      { label: "Brand Year", value: "2025" },
    ],
    []
  );

  return (
    <div ref={rootRef} data-testid="franchise-page" className="bg-cream min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-20 bg-cream grain">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 reveal">
            <Link
              to="/"
              data-testid="franchise-back-home"
              className="inline-flex items-center gap-2 text-cocoa-soft hover:text-cocoa transition-colors text-sm"
            >
              <ArrowLeft size={16} /> Back to home
            </Link>

            <div className="mt-6 text-[11px] uppercase tracking-[0.3em] text-gold font-semibold">
              Franchise Program · 2025
            </div>
            <h1 className="font-display mt-4 text-5xl md:text-6xl lg:text-7xl text-cocoa leading-[1.02] tracking-tight">
              Own a piece of{" "}
              <span className="gold-underline">Andhra&rsquo;s</span> most
              stubborn biryani brand.
            </h1>
            <p className="mt-6 text-cocoa-soft text-base md:text-lg leading-relaxed max-w-2xl">
              Three operating models, one obsession with the dum. Whether you
              want a 200-sq-ft express counter or a flagship dine-in destination
              — we&rsquo;ll set up your kitchen, train your team and supply the
              masala from our central commissary.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => handleInquire("")}
                data-testid="franchise-hero-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C8A14B] text-[#3A1B0E] hover:bg-[#D4A24E] hover:-translate-y-0.5 transition-all px-7 py-3.5 font-semibold shadow-md"
              >
                <Send size={16} /> Start an Inquiry
              </button>
              <a
                href="#franchise-models"
                className="inline-flex items-center justify-center rounded-full border-2 border-[#C8A14B] text-cocoa hover:bg-[#C8A14B] hover:text-[#FAF3E7] transition-all px-7 py-3 font-semibold"
              >
                View Models
              </a>
            </div>

            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-xl">
              {stats.map((s) => (
                <div key={s.label} data-testid={`franchise-stat-${s.label}`}>
                  <div className="font-display text-3xl text-gold">{s.value}</div>
                  <div className="text-[10px] uppercase tracking-wider text-cocoa-soft mt-1">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative reveal">
            <div className="absolute -inset-6 rounded-[2.2rem] bg-gradient-to-tr from-[#C8A14B]/30 via-transparent to-[#C0392B]/15 blur-2xl" />
            <div className="relative rounded-[2rem] overflow-hidden bg-[#3A1B0E] ring-1 ring-[#C8A14B]/60 shadow-2xl">
              <img
                src={BRAND.heroPhoto}
                alt="Andhra Biryani Co flagship"
                className="w-full h-[460px] md:h-[560px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#3A1B0E]/35 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 text-[#FAF3E7]">
                <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4A24E]">
                  Hyderabad Flagship
                </div>
                <div className="font-display text-2xl mt-1">
                  Jubilee Hills · Est. 2025
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY US strip */}
      <section className="bg-cream-deep py-10 border-y border-[#E8D8C0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 lg:grid-cols-4 gap-6 text-sm">
          {[
            { icon: Sparkles, label: "Heirloom recipe IP & masala library" },
            { icon: Truck, label: "Central commissary + weekly supply" },
            { icon: Users, label: "Hands-on team training (8 weeks)" },
            { icon: TrendingUp, label: "Aggregator & marketing playbook" },
          ].map(({ icon: I, label }) => (
            <div key={label} className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-white border border-[#C8A14B]/40 inline-flex items-center justify-center text-gold">
                <I size={16} />
              </span>
              <span className="text-cocoa font-medium">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* MODELS */}
      <section id="franchise-models" className="bg-cream py-20 md:py-28 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto reveal">
            <div className="text-[11px] uppercase tracking-[0.3em] text-gold font-semibold">
              Choose Your Format
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-cocoa mt-3 leading-tight">
              Three franchise models.{" "}
              <span className="gold-underline">One recipe</span>.
            </h2>
            <p className="mt-5 text-cocoa-soft">
              Pick the format that fits your real estate, capital and ambition.
              We&rsquo;ll match you with the right unit economics and the right
              opening city.
            </p>
          </div>

          <div className="mt-14 space-y-10">
            {MODELS.map((m, idx) => (
              <ModelCard key={m.id} m={m} idx={idx} onInquire={handleInquire} />
            ))}
          </div>
        </div>
      </section>

      {/* INQUIRY FORM */}
      <section
        id="franchise-inquiry"
        ref={formRef}
        className="bg-cocoa text-[#FAF3E7] py-20 md:py-28 grain scroll-mt-24"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4A24E] font-semibold">
              Franchise Inquiry
            </div>
            <h2 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
              Let&rsquo;s build a kitchen together.
            </h2>
            <p className="mt-5 text-[#FAF3E7]/75 leading-relaxed">
              Share a few details and our franchise team will get back to you
              within 24 hours with the unit economics, sample P&amp;L and the
              full investment breakdown.
            </p>

            <div className="mt-8 space-y-3 text-sm">
              <div className="flex gap-2 items-center">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4A24E]" />
                <span className="text-[#FAF3E7]/85">Confidential — no spam, ever.</span>
              </div>
              <div className="flex gap-2 items-center">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4A24E]" />
                <span className="text-[#FAF3E7]/85">First reply within 24 hours.</span>
              </div>
              <div className="flex gap-2 items-center">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4A24E]" />
                <span className="text-[#FAF3E7]/85">Full P&amp;L shared on call.</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {success ? (
              <div
                data-testid="franchise-inquiry-success"
                className="bg-[#FAF3E7] text-cocoa rounded-2xl p-8 md:p-10"
              >
                <div className="w-14 h-14 rounded-full bg-[#E8F7EA] flex items-center justify-center text-[#2E7D32]">
                  <CheckCircle2 size={28} />
                </div>
                <h3 className="font-display text-2xl mt-5">
                  Inquiry received, {success.name}.
                </h3>
                <p className="mt-3 text-cocoa-soft">
                  Our franchise team will reach out within 24 hours on{" "}
                  <strong className="text-cocoa">{success.phone}</strong>. Your
                  reference id is{" "}
                  <code className="px-2 py-0.5 rounded bg-[#FFF4D6] text-cocoa text-xs">
                    {success.id.slice(0, 8).toUpperCase()}
                  </code>
                  .
                </p>
                <button
                  onClick={() => setSuccess(null)}
                  data-testid="franchise-submit-another"
                  className="mt-6 inline-flex items-center gap-2 rounded-full border-2 border-[#C8A14B] text-cocoa hover:bg-[#C8A14B] hover:text-[#FAF3E7] transition-all px-5 py-2.5 text-sm font-semibold"
                >
                  Submit Another Inquiry
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="franchise-form"
                className="bg-[#FAF3E7] text-cocoa rounded-2xl p-8 md:p-10"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-cocoa-soft font-semibold">
                      Full Name *
                    </label>
                    <input
                      value={form.name}
                      onChange={onChange("name")}
                      data-testid="franchise-input-name"
                      type="text"
                      className="mt-2 w-full rounded-lg border border-cream-deep bg-white px-4 py-3 text-cocoa focus:outline-none focus:border-[#C8A14B] focus:ring-2 focus:ring-[#C8A14B]/30"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-cocoa-soft font-semibold">
                      Phone *
                    </label>
                    <input
                      value={form.phone}
                      onChange={onChange("phone")}
                      data-testid="franchise-input-phone"
                      type="tel"
                      placeholder="+91 ..."
                      className="mt-2 w-full rounded-lg border border-cream-deep bg-white px-4 py-3 text-cocoa focus:outline-none focus:border-[#C8A14B] focus:ring-2 focus:ring-[#C8A14B]/30"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-cocoa-soft font-semibold">
                      Email
                    </label>
                    <input
                      value={form.email}
                      onChange={onChange("email")}
                      data-testid="franchise-input-email"
                      type="email"
                      placeholder="you@example.com"
                      className="mt-2 w-full rounded-lg border border-cream-deep bg-white px-4 py-3 text-cocoa focus:outline-none focus:border-[#C8A14B] focus:ring-2 focus:ring-[#C8A14B]/30"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-cocoa-soft font-semibold">
                      City *
                    </label>
                    <input
                      value={form.city}
                      onChange={onChange("city")}
                      data-testid="franchise-input-city"
                      type="text"
                      placeholder="Hyderabad, Bengaluru..."
                      className="mt-2 w-full rounded-lg border border-cream-deep bg-white px-4 py-3 text-cocoa focus:outline-none focus:border-[#C8A14B] focus:ring-2 focus:ring-[#C8A14B]/30"
                      required
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label className="text-xs uppercase tracking-wider text-cocoa-soft font-semibold">
                    Preferred Model *
                  </label>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {MODELS.map((m) => {
                      const active = form.model === m.id;
                      const I = m.icon;
                      return (
                        <label
                          key={m.id}
                          data-testid={`franchise-model-radio-${m.id}`}
                          className={`relative cursor-pointer rounded-xl border px-4 py-3 transition-all ${
                            active
                              ? "border-[#C8A14B] bg-[#FFF8E6]"
                              : "border-cream-deep hover:bg-[#FDF5E2]"
                          }`}
                        >
                          {m.recommended && (
                            <span className="absolute -top-2 -right-2 text-[9px] uppercase tracking-wider font-bold bg-[#C0392B] text-white px-2 py-0.5 rounded-full shadow">
                              Recommended
                            </span>
                          )}
                          <input
                            type="radio"
                            name="model"
                            value={m.id}
                            checked={active}
                            onChange={onChange("model")}
                            className="sr-only"
                          />
                          <I size={18} className="text-gold" />
                          <div className="mt-2 text-sm font-semibold text-cocoa leading-tight">
                            {m.name}
                          </div>
                          <div className="text-[11px] text-cocoa-soft mt-1">
                            {m.specs[1].value}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-5">
                  <label className="text-xs uppercase tracking-wider text-cocoa-soft font-semibold">
                    Investment Range
                  </label>
                  <select
                    value={form.investment_range}
                    onChange={onChange("investment_range")}
                    data-testid="franchise-input-investment"
                    className="mt-2 w-full rounded-lg border border-cream-deep bg-white px-4 py-3 text-cocoa focus:outline-none focus:border-[#C8A14B] focus:ring-2 focus:ring-[#C8A14B]/30"
                  >
                    <option value="">Select a range</option>
                    <option value="5-10L">₹5 – 10 Lakh</option>
                    <option value="10-20L">₹10 – 20 Lakh</option>
                    <option value="20-30L">₹20 – 30 Lakh</option>
                    <option value="30L+">₹30 Lakh+</option>
                  </select>
                </div>

                <div className="mt-5">
                  <label className="text-xs uppercase tracking-wider text-cocoa-soft font-semibold">
                    Anything we should know?
                  </label>
                  <textarea
                    value={form.message}
                    onChange={onChange("message")}
                    data-testid="franchise-input-message"
                    rows={4}
                    placeholder="Background, real estate already secured, F&B experience..."
                    className="mt-2 w-full rounded-lg border border-cream-deep bg-white px-4 py-3 text-cocoa focus:outline-none focus:border-[#C8A14B] focus:ring-2 focus:ring-[#C8A14B]/30 resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="franchise-submit-btn"
                  className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-[#C0392B] text-white hover:bg-[#D03A2A] disabled:opacity-60 disabled:cursor-not-allowed hover:-translate-y-0.5 transition-all px-7 py-3.5 font-semibold shadow-md"
                >
                  <Send size={16} />
                  {submitting ? "Submitting…" : "Submit Inquiry"}
                </button>
                <p className="mt-3 text-xs text-cocoa-soft">
                  By submitting, you agree to be contacted by our franchise team
                  via phone or email.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
